import { formatCents, formatCount, tt } from '@agos/shared'

/**
 * CardPay: what the platform takes on a card payment, and what it pays to take it.
 *
 * Three rates, and only two of them are the platform's to set. `markupPct` is per franchise;
 * `tbCostPct` is what Tatra Banka charges and is not negotiable from this screen. The margin is the
 * difference, and **it can go negative** — a franchise whose markup is set below the bank's cost means
 * the platform pays to process that restaurant's card payments.
 *
 * Measured: `defaultMarkupPct: 2.0` and `tbCostPct: 1.6`. SAAS.md §3 describes "1.8%/1.6%", so the
 * configured rate has moved since that note was written — both are read from the response and neither
 * is hardcoded.
 *
 * **The cent figures are read, never recomputed.** Franchise 1's 386 payments come to `markupCents:
 * 16 001` against 15 999,6 for a flat 2 % of the gross, which is the signature of rounding per payment.
 * Recomputing from the total would disagree with the invoice by a cent or two every month, and the
 * invoice is the one that is right.
 */

export interface CardPayTotals {
  payments: number
  grossCents: number
  markupCents: number
  payoutCents: number
  tbCostCents: number
  marginCents: number
  refundedCount: number
  refundedCents: number
  clawbackCents: number
  netPayoutCents: number
}

export interface CardPayFranchise {
  franchiseId: number
  name: string
  slug: string
  markupPct: number
  tbCostPct: number
  totals: CardPayTotals
  daily: (CardPayTotals & { day: string })[]
  /**
   * The refunds themselves - `SAAS.md` §3.42.
   *
   * **The endpoint has always answered these and nothing read them.** §3.28 fixed this screen's
   * margin identity by adding `reversedMarkupCents`, the markup handed back when a payment is
   * refunded, computed from the daily totals - so the page has carried arithmetic about refunds
   * since then and never shown one. Measured: **30 refunded TatraPay payments totalling 551,65 EUR**
   * against 1 249 paid at 26 417,25.
   */
  refunds: CardPayRefund[]
  /**
   * Why this franchise's report could not be read, or null - `SAAS.md` §3.44.
   *
   * **`CardpayReportService` records the failure on purpose** - *"One franchise whose database is
   * down must not take the whole report with it, the rest of the platform's reconciliation is still
   * owed"* - and nothing read it. So an unreachable franchise rendered with `totals` all nought and a
   * **plausible margin**, because `markupPct` and `tbCostPct` come from the settings rather than the
   * failed query: 0,00 EUR at 1 % is exactly what a franchise with no card payments looks like.
   *
   * **FB-545 for the third time.** `franchise-stats.ts` already carries the pattern, with the rule
   * *"a zero the screen cannot vouch for is worse than no figure"*; this is the same field and the
   * same treatment.
   */
  error: string | null
}

/** One refunded payment, as `CardpayReportService` sends it - newest first, capped server-side. */
export interface CardPayRefund {
  id: number
  orderNumber: string
  amountCents: number
  /**
   * What the refund costs the **platform**, which is not what it costs the customer.
   *
   * A 20,00 EUR refund does not cost the platform 20,00 - it costs the platform its markup on it,
   * and that is the figure §3.28's margin identity turns on. Shown beside the amount for that
   * reason: the two answer different questions and a list of amounts alone would invite the wrong one.
   */
  clawbackCents: number
  paidAt: string
  refundedAt: string
}

export interface CardPayReport {
  defaultMarkupPct: number
  tbCostPct: number
  platform: CardPayTotals
  franchises: CardPayFranchise[]
}

function num(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function totalsOf(value: unknown): CardPayTotals {
  const row = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>
  return {
    payments: num(row.payments),
    grossCents: num(row.grossCents),
    markupCents: num(row.markupCents),
    payoutCents: num(row.payoutCents),
    tbCostCents: num(row.tbCostCents),
    marginCents: num(row.marginCents),
    refundedCount: num(row.refundedCount),
    refundedCents: num(row.refundedCents),
    clawbackCents: num(row.clawbackCents),
    netPayoutCents: num(row.netPayoutCents),
  }
}

export function cardPayReportOf(payload: unknown): CardPayReport {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const franchises = Array.isArray(row.franchises) ? row.franchises : []
  return {
    defaultMarkupPct: num(row.defaultMarkupPct),
    tbCostPct: num(row.tbCostPct),
    platform: totalsOf(row.platform),
    franchises: franchises.map((entry) => {
      const franchise = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
      const daily = Array.isArray(franchise.daily) ? franchise.daily : []
      return {
        franchiseId: num(franchise.franchiseId),
        name: typeof franchise.name === 'string' ? franchise.name : '',
        slug: typeof franchise.slug === 'string' ? franchise.slug : '',
        markupPct: num(franchise.markupPct),
        tbCostPct: num(franchise.tbCostPct),
        totals: totalsOf(franchise.totals),
        refunds: refundsOf(franchise.refunds),
        error: typeof franchise.error === 'string' && franchise.error !== '' ? franchise.error : null,
        daily: daily.map((day) => {
          const dayRow = (typeof day === 'object' && day !== null ? day : {}) as Record<string, unknown>
          return {
            ...totalsOf(dayRow),
            day: typeof dayRow.day === 'string' ? dayRow.day : '',
          }
        }),
      }
    }),
  }
}

/** Whether this franchise's figures are measurements at all - §3.44. */
export function isUnreachable(franchise: CardPayFranchise): boolean {
  return franchise.error !== null
}

/**
 * Whether its numbers may be shown as numbers.
 *
 * Named as the positive so the call sites read as a condition on the figures rather than a negation,
 * which is how `franchise-stats.ts` words the same thing.
 */
export function showsFigures(franchise: CardPayFranchise): boolean {
  return !isUnreachable(franchise)
}

/** What to say instead. The server's own sentence, because it names the cause. */
export function unreachableNote(franchise: CardPayFranchise): string {
  return franchise.error === null ? '' : `${tt('Nedostupné')} - ${franchise.error}`
}

function refundText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return ''
}

/** `refunds[]` off a franchise entry - §3.42. Already cents on the wire, unlike the money elsewhere. */
export function refundsOf(value: unknown): CardPayRefund[] {
  if (!Array.isArray(value)) return []
  return (value as Record<string, unknown>[]).flatMap((row) =>
    typeof row.id === 'number'
      ? [
          {
            id: row.id,
            orderNumber: refundText(row.orderNumber),
            amountCents: num(row.amountCents),
            clawbackCents: num(row.clawbackCents),
            paidAt: refundText(row.paidAt),
            refundedAt: refundText(row.refundedAt),
          },
        ]
      : [],
  )
}

/**
 * What the refunds come to, both ways - read off the window's aggregate, never off the rows.
 *
 * **Two sums rather than one, because they are two questions.** `amountCents` is what went back to
 * customers; `clawbackCents` is what it cost the platform. §3.28's identity needs the second and a
 * reader wants the first, and quoting one as the other is the mistake this pairing prevents.
 *
 * **It takes the aggregate rather than the list, and that is FB-935 - `SAAS.md` §3.172.** This
 * summed `franchise.refunds`, and the server caps that list at `REFUND_DETAIL_LIMIT = 100` with no
 * total of its own: above a hundred refunds in the window the count, the amount returned to
 * customers and the platform's own clawed-back margin were all silently short by however many rows
 * fell off the end, while the platform `StatCard` and the daily `Refundy` column beside them stayed
 * right. `franchise.totals` is summed from the daily `GROUP BY` rows, so it is the whole window at
 * any size, and all three names were already on the wire.
 *
 * **It also makes the summary agree with the table above it.** `percent()` rounds HALF_UP per row,
 * so `sum(percent(each))` is not `percent(sum)` - the old summary could differ from the daily column
 * by cents even under a hundred rows, and both figures claimed to be the same thing.
 *
 * Deliberately **not** `readonly CardPayRefund[] | CardPayTotals`: a union would let the call site
 * keep passing the rows and typecheck, and there is exactly one call site.
 */
export function refundTotals(totals: CardPayTotals): {
  count: number
  amountCents: number
  clawbackCents: number
} {
  return {
    count: totals.refundedCount,
    amountCents: totals.refundedCents,
    clawbackCents: totals.clawbackCents,
  }
}

/**
 * *Zobrazených 100 z 180*, or empty when the table is the whole window - `SAAS.md` §3.172.
 *
 * **Once `refundTotals` reads the aggregate the two figures differ visibly** - *Vrátené 180* over a
 * table of a hundred rows - and a visible difference with no sentence is the state §3.48 wrote
 * `truncationNote` for on the audit log.
 *
 * **Not `truncationNote` itself, and the reason is that helper's own docstring.** It renders
 * `"100 z 347 záznamov"` - *records* - and scopes itself to the audit trail, *"the one screen where
 * nothing happened before this and we stopped showing you must not look the same"*. Printing
 * *záznamov* over a money table would be wrong, and giving it a noun parameter would edit the audit
 * page, its three pinned cases and its `data-audit-truncated` marker for no gain on either screen.
 * **The shared thing is the rule, and the rule has no shared sentence because the two screens count
 * different objects.**
 *
 * Empty when nothing is hidden, because a note on every card is a note nobody reads.
 */
export function refundListNote(shown: number, totals: CardPayTotals): string {
  if (totals.refundedCount <= shown) return ''
  return `${tt('Zobrazených')} ${formatCount(shown)} ${tt('z')} ${formatCount(totals.refundedCount)}`
}

/** One figure of a franchise's month, with what to call it. */
export interface CardPayTotalFigure {
  label: string
  cents: number
  /** The marker the screen hangs on it, so a spec names the figure rather than its position. */
  hook: string
}

/**
 * A franchise's month, column for column - `SAAS.md §3.113`.
 *
 * **Legacy's `<tfoot>`, which this page had no counterpart for.** Its daily table closes with a bold
 * *Spolu* row repeating every column; AGOS drew the days and stopped, so *what do we send this franchise
 * for the month* was a sum done by eye down a column of up to 31 rows. On a payout screen that is the
 * only question, and the figures are already on the wire - `franchise.totals` carries all of them.
 *
 * **Read, never recomputed**, which is this file's standing rule and the reason it exists: franchise 1's
 * 386 payments come to `markupCents: 16 001` against 15 999,6 for a flat 2 % of the gross, because the
 * server rounds per payment. Summing the day rows here would disagree with the invoice by a cent or two
 * every month, and the invoice is the one that is right.
 *
 * **`payments` is deliberately absent** - it is a count, not money, and the screen draws it beside the
 * name already. Mixing a count into a row of euros is how a total gets read as a price.
 */
export function monthTotals(franchise: CardPayFranchise): CardPayTotalFigure[] {
  const totals = franchise.totals
  return [
    { label: 'CardPay 100 %', cents: totals.grossCents, hook: 'gross' },
    { label: tt('Provízia'), cents: totals.markupCents, hook: 'markup' },
    { label: tt('Na vyplatenie'), cents: totals.payoutCents, hook: 'payout' },
    { label: tt('Refundy'), cents: totals.refundedCents, hook: 'refunded' },
    { label: tt('Netto poslať'), cents: totals.netPayoutCents, hook: 'net' },
    { label: tt('Poplatok banky'), cents: totals.tbCostCents, hook: 'tb' },
    { label: tt('Podiel'), cents: totals.marginCents, hook: 'margin' },
  ]
}

/** The rate the platform keeps, in points. Negative means it is paying to process. */
export function marginPct(franchise: CardPayFranchise): number {
  return franchise.markupPct - franchise.tbCostPct
}

export function isLosing(totals: CardPayTotals): boolean {
  return totals.marginCents < 0
}

/**
 * Said beside a negative margin, in words.
 *
 * Legacy colours it red — `marginCents >= 0 ? "text-emerald-300" : "text-red-300"` in four places, which
 * is the right instinct. Colour alone is the signal a tired person misses on a dashboard of numbers, and
 * this particular one going red means the platform is paying to process somebody else's card payments.
 */
export function marginWarning(franchise: CardPayFranchise): string {
  if (marginPct(franchise) >= 0) return ''
  return `${tt('Provízia je nižšia než poplatok banky - platforma na tejto franšíze prerába')}.`
}

/**
 * The markup the platform stops earning because a payment was refunded - §3.28.
 *
 * `CardpayReportService` computes it as `refundedCents - clawbackCents`, and the reason is in its own
 * comment: *"The customer's markup goes back to the customer, so it is not clawed back"* - the bank
 * takes back the payment minus the markup, so the difference **is** the markup on the refunded
 * amount, and the platform no longer has it.
 *
 * Named rather than inlined into the identity below, because an arithmetic rule with a name can be
 * tested and one inlined at its only call site gets re-derived slightly differently the next time
 * somebody needs it - which is how the identity came to disagree with the server in the first place.
 */
export function reversedMarkupCents(totals: CardPayTotals): number {
  return totals.refundedCents - totals.clawbackCents
}

/**
 * The identities the response satisfies, so a payload that does not is visible rather than drawn.
 *
 * **The second one used to omit the reversal and therefore called correct totals wrong** - §3.28.
 * `marginCents` is `markupCents - tbCostCents - reversedMarkup`, not `markupCents - tbCostCents`, so
 * on any period containing a refund this reported an inconsistency that was not there. The owner's
 * franchise 1 has **30** refunded payments against a 2 % markup, so it fired on real data.
 *
 * A warning that cries wolf on a money dashboard is one the reader learns to ignore, and this
 * warning is the only thing that would surface a real inconsistency.
 */
export function totalsAreConsistent(totals: CardPayTotals): boolean {
  return (
    totals.payoutCents === totals.grossCents - totals.markupCents &&
    totals.marginCents === totals.markupCents - totals.tbCostCents - reversedMarkupCents(totals)
  )
}

export function inconsistentNote(totals: CardPayTotals): string {
  if (totalsAreConsistent(totals)) return ''
  return tt('Súčty z tohto obdobia nesedia - čísla sú zobrazené tak, ako prišli.')
}

/** `386 platieb · 7 999,80 €` — the count and the volume, which answer different questions. */
export function volumeNote(totals: CardPayTotals): string {
  return `${formatCount(totals.payments)} ${tt('platieb')} · ${formatCents(totals.grossCents)}`
}

/** One day of one franchise's card payments - the row type of `CardPayFranchise.daily`, §3.93. */
export type CardPayDay = CardPayTotals & { day: string }
